import React from 'react';
import { Modal, Typography, Form, Input, Button, Flex, theme, message } from 'antd';

import { useRequestUpgradeFromAdminsMutation } from '~features/billing/api/billingApiSlice';

interface RequestUpgradeFromAdminsModalProps {
  open: boolean;
  onClose: () => void;
}

const MESSAGE_MAX_LENGTH = 500;
const ERROR_MESSAGE = 'Failed to send message. Please try again later.';
const SUCCESS_MESSAGE = 'Message sent';

function RequestUpgradeFromAdminsModal({ open, onClose }: RequestUpgradeFromAdminsModalProps) {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const [messageApi, context] = message.useMessage();
  const [requestUpgradeFromAdmins, { isLoading: isRequestingUpgrade }] = useRequestUpgradeFromAdminsMutation();

  const handleSubmit = async (values: { requesterMessage?: string }) => {
    try {
      await requestUpgradeFromAdmins({ requesterMessage: values.requesterMessage }).unwrap();

      form.resetFields();

      messageApi.success(SUCCESS_MESSAGE);
      onClose();
    } catch (error) {
      messageApi.error(ERROR_MESSAGE);
    }
  };

  const handleCancel = () => {
    if (isRequestingUpgrade) {
      return; // Prevent canceling during request
    }
    form.resetFields();
    onClose();
  };

  return (
    <>
      {context}
      <Modal
        title="Request an upgrade to unlock this feature"
        open={open}
        onCancel={handleCancel}
        footer={null}
        width={520}
        destroyOnHidden
        closable={!isRequestingUpgrade}
        maskClosable={!isRequestingUpgrade}
      >
        <div style={{ marginBottom: token.marginMD }}>
          <Typography.Text>Let a workspace admin know you&apos;re interested in using this feature.</Typography.Text>
        </div>

        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Form.Item label={<Typography.Text strong>Add a message (optional)</Typography.Text>} name="requesterMessage">
            <Input.TextArea
              placeholder="Enter your message"
              autoSize={{ minRows: 3, maxRows: 8 }}
              maxLength={MESSAGE_MAX_LENGTH}
            />
          </Form.Item>

          <Flex justify="end" gap={token.marginSM}>
            <Button onClick={handleCancel} type="text" disabled={isRequestingUpgrade}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={isRequestingUpgrade}>
              Submit request
            </Button>
          </Flex>
        </Form>
      </Modal>
    </>
  );
}

export default RequestUpgradeFromAdminsModal;
